import { getPage, getPageType, getGlobal, getAllPagesForSlugComponent } from 'lib/contentful';
import { fetchAllLocales } from 'lib/contentful/api';
import { useSalons } from 'hooks';
import SEO from 'components/shared/SEO';
import PageGeneral from 'components/pages/general';
import PageCollection from 'components/pages/collection';
import Layout from 'components/layout';
import {
  reformatContentfulLocaleToLocale,
  reformatLocaleToContentfulLocale,
} from 'lib/utils/helpers';
import getProductsSelling from 'lib/shopify/getProductsSelling';
import PageCollaborator from 'components/pages/collaborator/collaborator';
import sortProductsBySelling from 'lib/utils/sortProductsBySelling';
import CountrySelectorModal from '../components/pages/countrySelector';
import { db } from '../lib/firebase';

export default function Page({ page, locale, type, previewData, globalModules }) {
  useSalons(locale);

  // // If the page is not yet generated, this will be displayed
  // // initially until getStaticProps() finishes running
  // if (router.isFallback) {
  //   return <div>Loading...</div>;
  // }

  const renderPageContent = () => {
    switch (type) {
      case 'PageGeneral':
        return <PageGeneral locale={locale} data={page} resources={globalModules} />;
      case 'PageCollection':
        return <PageCollection locale={locale} data={page} resources={globalModules} />;
      case 'PageCollaborator':
        return <PageCollaborator locale={locale} data={page} resources={globalModules} />;
      default:
        return <div>{`Cannot find page type ${type}`}</div>;
    }
  };

  return (
    <Layout locale={locale} pageData={page} previewData={previewData} globalModules={globalModules}>
      <>
        {page?.seo && <SEO seo={page.seo} pageData={page} />}
        {renderPageContent()}
        <CountrySelectorModal globalModules={globalModules} pageData={page} locale={locale} />
      </>
    </Layout>
  );
}

export async function getStaticProps({ locale, params, preview = false, previewData = null }) {
  const { slug } = params;
  const reformattedLocale = reformatLocaleToContentfulLocale(locale);
  const type = await getPageType(slug, reformattedLocale);
  let productsSelling;
  if (type === 'PageCollection') {
    productsSelling = await getProductsSelling(locale);
  }
  const page = await getPage(type, slug, reformattedLocale, preview, productsSelling);

  if (!page) {
    const salonsResponse = await db
      .collection('salons')
      .where('kickbackFeeCode', '==', slug.toLowerCase())
      .get();
    const salons = salonsResponse?.docs ? salonsResponse.docs.map(doc => doc.data()) : [];
    const salon = salons?.[0];

    if (salon && salon.kickbackFeeCode) {
      return {
        redirect: {
          destination: `/salon/${salon.kickbackFeeCode}/`,
          permanent: false,
        },
      };
    }

    return {
      notFound: true,
    };
  }

  const globalModuleNames = [
    'cookies',
    'announcement-bar',
    'countryselector',
    'firstcountryselectorlocales',
  ];

  if (type === 'PageGeneral') {
    globalModuleNames.push('secondpartproductpage');
  }

  if (type === 'PageCollection') {
    globalModuleNames.push('collection');
    globalModuleNames.push('secondpartproductpage');
  }

  if (type === 'PageCollaborator') {
    globalModuleNames.push('collaborator');
  }

  const globalModules = await getGlobal(globalModuleNames, reformattedLocale);

  // if page is a collection, automatically sort products by sales
  if (
    type === 'PageCollection' &&
    page?.products?.length > 0 &&
    page?.autoSortProductsBySales !== false
  ) {
    page.products = sortProductsBySelling(page?.products, productsSelling);
  }

  return {
    props: { page, locale, type, previewData, globalModules },
  };
}

export async function getStaticPaths() {
  const contentfulLocales = await fetchAllLocales();

  /*
  * contentfulLocales [
  'en',    'nl-NL', 'nl-BE',
  'hr',    'de-DE', 'fr',
  'sr',    'sl',    'en-US',
  'fr-BE', 'fr-FR', 'bs',
  'en-GB', 'es-ES', 'ro'
] */

  const allPages = [];
  const pagesPromises = [];

  const saveLocale = async locale => {
    const allPagesForLocale = await getAllPagesForSlugComponent(locale);
    allPages.push(...allPagesForLocale);
  };

  contentfulLocales.forEach(locale => {
    pagesPromises.push(saveLocale(reformatContentfulLocaleToLocale(locale)));
  });

  await Promise.all(pagesPromises);

  const finalPaths = [];

  allPages.forEach(page => {
    finalPaths.push({
      params: {
        slug: page.slug,
      },
      locale: page.locale,
    });
  });

  return {
    paths: finalPaths,
    fallback: 'blocking',
  };
}
